/**
 * Insights context (read model), pure display helpers. No I/O. Kept inside
 * Insights: read models import nothing from other modules (04-architecture.md
 * §2), so the money format is a small local copy rather than a cross-context
 * dependency on Catalog (same stance as discovery/domain/format.ts).
 *
 * Weekday convention matches the booking columns: 0 = Monday … 6 = Sunday
 * (03-core-model.md; booking.bookings.weekday check `between 0 and 6`).
 */

const WEEKDAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

/** "Monday" for weekday 0 … "Sunday" for 6; "" if out of range. */
export function weekdayLabel(weekday: number): string {
  return WEEKDAY_NAMES[weekday] ?? "";
}

/** Minutes-from-midnight → "09:00" / "14:30". */
export function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** A peak Time Block label, e.g. "Monday 09:00–10:00". */
export function blockLabel(
  weekday: number,
  startMinutes: number,
  endMinutes: number,
): string {
  return `${weekdayLabel(weekday)} ${minutesToTime(startMinutes)}–${minutesToTime(endMinutes)}`;
}

/** Integer cents → "$25" / "$25.50" (thousands separated). */
export function formatUsd(cents: number): string {
  const dollars = cents / 100;
  const hasCents = cents % 100 !== 0;
  const money = dollars.toLocaleString("en-US", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return `$${money}`;
}

/**
 * A whole-number percent of `part` out of `whole`, e.g. "38%". A zero (or
 * negative) whole has no defined rate, shown as "n/a" rather than a dash: a
 * lone dash in a stat tile reads as a design flourish, not as an answer.
 */
export function formatPercent(part: number, whole: number): string {
  if (whole <= 0) return "n/a";
  return `${Math.round((part / whole) * 100)}%`;
}
